import type { AreaKey, ReportSummary } from '@/api/types';

export interface Session { assessmentId: string; childId: string; childName: string; createdAt: string; reports: ReportSummary[] }

const AREA_ORDER: Record<AreaKey, number> = {
  milestones: 0,
  adaptive: 1,
  connecting: 2,
  attention: 3,
  teen: 4,
  sensory: 5,
};

/** Falls back to the end of the order for a report with no (or an unrecognised) area key, so an
 * older client or a not-yet-known area never throws — it just sorts last. */
const areaOrder = (key: AreaKey | undefined): number => (key && key in AREA_ORDER ? AREA_ORDER[key] : 99);

/** Reports in the fixed area order (milestones, everyday independence, connecting, attention,
 * teen, sensory), then by creation time. */
export const sortByArea = (reports: ReportSummary[]): ReportSummary[] =>
  [...reports].sort((a, b) => (areaOrder(a.assessmentKey) - areaOrder(b.assessmentKey)) || a.createdAt.localeCompare(b.createdAt));

/** One session per assessment id, newest first; reports inside in area order. */
export const groupSessions = (reports: ReportSummary[]): Session[] => {
  const map = new Map<string, Session>();
  for (const r of reports) {
    const s = map.get(r.assessmentId) ?? { assessmentId: r.assessmentId, childId: r.childId, childName: r.childName, createdAt: r.createdAt, reports: [] };
    s.reports.push(r);
    if (r.createdAt < s.createdAt) s.createdAt = r.createdAt;
    map.set(r.assessmentId, s);
  }
  return [...map.values()]
    .map((s) => ({ ...s, reports: sortByArea(s.reports) }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
};
